<template>
  <DefaultModal :title="getSubtitle()" ref="modal" :hideSaveButton="true" :width="800">
    <template v-slot:content>
      <v-container>
        <v-row>
          <v-col cols="12">
            <v-data-table
              :headers="headers"
              :items="poolStatuses"
              class="elevation-1"
              :loading="loading"
              hide-default-footer
              :items-per-page="-1"
            >
              <template v-slot:top>
                <v-toolbar flat>
                  <v-toolbar-title>{{ getTableTitle() }}</v-toolbar-title>
                  <v-spacer></v-spacer>
                  <v-btn
                    color="primary"
                    dark
                    @click="newPoolStatus"
                  >
                    {{ $t('add') }}
                  </v-btn>
                </v-toolbar>
              </template>

              <template v-slot:item.pool_id="{ item }" v-if="mode === 'status'">
                {{ getPoolTitle(item.pool_id) }}
              </template>

              <template v-slot:item.status_id="{ item }" v-if="mode === 'pool'">
                {{ getStatusTitle(item.status_id) }}
              </template>

              <template v-slot:item.actions="{ item }">
                <v-tooltip left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-bind="attrs"
                      v-on="on"
                      class="mr-2"
                      @click="editPoolStatus(item)"
                    >
                      mdi-pencil
                    </v-icon>
                  </template>
                  <span>{{ $t('edit') }}</span>
                </v-tooltip>

                <v-tooltip left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-bind="attrs"
                      v-on="on"
                      @click="deletePoolStatus(item)"
                    >
                      mdi-delete
                    </v-icon>
                  </template>
                  <span>{{ $t('delete') }}</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-container>

      <!-- Create/Edit Form Modal -->
      <DefaultModal :title="getFormModalTitle()" ref="formModal" @submit="savePoolStatus">
        <template v-slot:content>
          <DynamicFormContent :fields="formFields"></DynamicFormContent>
        </template>
      </DefaultModal>
    </template>
  </DefaultModal>
</template>

<script>
import { mapActions } from 'vuex'

import DefaultModal from '@/components/DefaultModal.vue'
import DynamicFormContent from '@/components/DynamicFormContent.vue'

import AppActions from '@/store/app/actions-types'

import PoolStatusAPI from '@/services/api/job/poolstatus'
import PoolAPI from '@/services/api/job/pool'
import StatusAPI from '@/services/api/job/status'

export default {
  name: "PoolStatus",

  components: {
    DefaultModal,
    DynamicFormContent
  },

  props: {
    mode: {
      type: String,
      required: true,
      validator: value => ['pool', 'status'].includes(value)
    },
    entityId: {
      type: [Number, String, null],
      required: false,
      default: null
    },
    entityTitle: {
      type: String,
      required: false,
      default: ''
    }
  },

  data() {
    return {
      loading: false,
      poolStatuses: [],
      selectedPoolStatus: null,
      pools: [],
      statuses: []
    }
  },

  computed: {
    headers() {
      const baseHeaders = [
        { text: this.$t('pool_status_order'), value: 'pool_status_order', sortable: true, width: '150px' }
      ]

      if (this.mode === 'status') {
        baseHeaders.unshift({ text: this.$t('pool'), value: 'pool_id', sortable: true })
      } else {
        baseHeaders.unshift({ text: this.$t('status'), value: 'status_id', sortable: true })
      }

      baseHeaders.push({ text: this.$t('actions'), value: 'actions', sortable: false, width: '120px' })

      return baseHeaders
    },

    formFields() {
      const fields = []

      if (this.mode === 'status') {
        // When viewing from status, allow selecting pools
        fields.push({
          key: 'pool_id',
          name: 'pool_id',
          label: this.$t('pool'),
          type: 'select',
          value: this.selectedPoolStatus ? this.selectedPoolStatus.pool_id : null,
          items: this.pools,
          itemText: 'pool_title',
          itemValue: 'pool_id',
          required: true,
          rules: [v => !!v || this.$t('field_required')],
          cols: 6
        })
      } else {
        // When viewing from pool, allow selecting statuses
        fields.push({
          key: 'status_id',
          name: 'status_id',
          label: this.$t('status'),
          type: 'select',
          value: this.selectedPoolStatus ? this.selectedPoolStatus.status_id : null,
          items: this.statuses,
          itemText: 'status_title',
          itemValue: 'status_id',
          required: true,
          rules: [v => !!v || this.$t('field_required')],
          cols: 6
        })
      }

      fields.push({
        key: 'pool_status_order',
        name: 'pool_status_order',
        label: this.$t('pool_status_order'),
        type: 'number',
        value: this.selectedPoolStatus ? this.selectedPoolStatus.pool_status_order : 0,
        rules: [v => v >= 0 || this.$t('must_be_positive')],
        cols: 6
      })

      return fields
    }
  },

  methods: {
    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE
    ]),

    openModal() {
      this.loadData()
      this.$refs.modal.openModal()
    },

    closeModal() {
      this.$refs.modal.closeModal()
    },

    async loadData() {
      this.loading = true
      
      try {
        // Always load dropdown data regardless of entityId validity
        if (this.mode === 'status') {
          // Load all pools for the dropdown
          const poolsResult = await PoolAPI.getAllPools({ limit: 1000 })
          this.pools = poolsResult.data.items || []
        } else {
          // Load all statuses for the dropdown
          const statusesResult = await StatusAPI.getAllStatuses({ order: 'status_title', direction: 'ASC', offset: 0, limit: 2000000000 })
          this.statuses = statusesResult.data.items || []
        }

        // Validate entityId before loading pool-status relationships
        if (!this.entityId || this.entityId === 'null' || this.entityId === null || this.entityId === undefined) {
          console.warn('Invalid entityId provided:', this.entityId)
          this.poolStatuses = []
          this.loading = false
          return
        }

        // Load pool statuses based on mode
        let poolStatusResult
        if (this.mode === 'status') {
          poolStatusResult = await PoolStatusAPI.getPoolStatusesFromStatus(this.entityId)
        } else {
          poolStatusResult = await PoolStatusAPI.getPoolStatusesFromPool(this.entityId)
        }

        this.poolStatuses = poolStatusResult.data.items || []
      } catch (error) {
        console.error('Error loading pool statuses:', error)
        this.poolStatuses = []
      } finally {
        this.loading = false
      }
    },

    newPoolStatus() {
      this.selectedPoolStatus = null
      this.$refs.formModal.openModal()
    },

    editPoolStatus(poolStatus) {
      this.selectedPoolStatus = poolStatus
      this.$refs.formModal.openModal()
    },

    savePoolStatus() {
      this.$refs.formModal.errorMessage = null
      this.$refs.formModal.errorList = []

      // Collect data from dynamic form fields
      const formData = {}
      this.formFields.forEach(field => {
        if (field.type === 'number') {
          formData[field.name] = field.value !== '' ? Number(field.value) : null
        } else {
          formData[field.name] = field.value
        }
      })

      // Add the fixed entity ID based on mode
      if (this.mode === 'status') {
        formData.status_id = this.entityId
      } else {
        formData.pool_id = this.entityId
      }

      let apiCall
      
      if (this.selectedPoolStatus) {
        const poolStatusId = this.selectedPoolStatus.pool_status_id
        apiCall = PoolStatusAPI.updatePoolStatus(poolStatusId, formData)
      } else {
        apiCall = PoolStatusAPI.createPoolStatus(formData)
      }

      apiCall
        .then(() => {
          const message = this.selectedPoolStatus ? 'Pool Status updated successfully' : 'Pool Status created successfully'
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t(message))
          this.$refs.formModal.closeModal()
          this.loadData()
        })
        .catch((error) => {
          this.$refs.formModal.submitting = false
          if (error.response && error.response.data) {
            this.$refs.formModal.errorMessage = error.response.data.message || 'An error occurred'
          }
        })
    },

    deletePoolStatus(poolStatus) {
      if (confirm(this.$t('confirm_delete'))) {
        PoolStatusAPI.deletePoolStatus(poolStatus.pool_status_id)
          .then(() => {
            this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('Pool Status deleted successfully'))
            this.loadData()
          })
          .catch((error) => {
            console.error('Error deleting pool status:', error)
          })
      }
    },

    getSubtitle() {
      if (this.mode === 'status') {
        return this.$t('pools_in_status', { status: this.entityTitle })
      } else {
        return this.$t('statuses_in_pool', { pool: this.entityTitle })
      }
    },

    getTableTitle() {
      return this.mode === 'status' ? this.$t('pools') : this.$t('statuses')
    },

    getFormModalTitle() {
      const action = this.selectedPoolStatus ? this.$t('edit') : this.$t('add')
      const entity = this.mode === 'status' ? this.$t('pool') : this.$t('status')
      return `${action} ${entity}`
    },

    getPoolTitle(poolId) {
      const pool = this.pools.find(p => p.pool_id === poolId)
      return pool ? pool.pool_title : poolId
    },

    getStatusTitle(statusId) {
      const status = this.statuses.find(s => s.status_id === statusId)
      return status ? status.status_title : statusId
    }
  }
}
</script>

<style scoped>
.container {
  padding: 0;
}
</style>
